import React, { Fragment, useState } from "react";

import { DetailsPane } from "@common/components/DetailsPane/DetailsPane";
import { getDropdown, GroupedInfoElement, readMetadata } from "@common/components/DetailsPane/helper";
import * as DetailsIcon from "@common/components/DetailsPane/icons";
import { UserIcon } from "@common/components/DetailsPane/icons";
import { DropdownItem } from "@common/components/Dropdown/Dropdown";
import Heading from "@common/components/Heading/Heading";
import { HelperText } from "@common/components/HelperText/HelperText";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useUsersAttributes } from "@common/hooks/filtering/modules/useUsersAttributes";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { AppUser, getUserName, PlatformName } from "@common/models";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { docLink } from "@common/util/docs";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import identifiers from "@common/util/identifiers.json";
import { showIntercomArticle } from "@common/util/intercom";
import useNotificationsApiConsumer from "@main/consumers/useNotificationsApiConsumer";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import { TicketDetailsContainer } from "@main/pages/shared/components/TicketDetails/TicketDetailsContainer";
import { TicketContainer } from "@main/pages/shared/components/TicketDetails/TicketDetailsContainer.styles";
import { TicketInfoContainer } from "@main/pages/shared/components/TicketDetails/TicketInfoContainer";
import * as Icon from "@main/pages/shared/icons/icons";
import * as Styled from "@main/pages/shared/styles/DetailsWrapper.styles";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import { ModeIcon, Subtitle, UserModeDropdown, UserTooltip } from "./UserDetails.styles";
import UserTicketCards from "../components/UserTicketCards";
import useUserDetailsPageApiConsumer from "../consumers/useUserDetailsPageApiConsumer";

export default function UserDetailsView() {
  const params = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const consumer = useUserDetailsPageApiConsumer({ userId: params.userId });
  const { unreadNotifications } = useNotificationsApiConsumer();
  const [detailDropdownOpened, setDetailDropdownOpened] = useState<string | undefined>(undefined);
  const [modeDropdownOpened, setModeDropdownOpened] = useState<boolean>(false);

  const { addExternal } = useUsersAttributes();

  const CustomElement = (
    <NavigationCustomElement>
      <Tooltip
        text={"See all users"}
        position={"bottom"}
      >
        <Styled.NavbarNavigation
          onClick={() =>
            navigate(
              generatePath(RoutePaths.USERS, {
                workspaceSlug: selectedWorkspace?.slug ?? null,
                appKey: selectedApp?.key ?? null,
              }),
            )
          }
        >
          <DetailsIcon.SendIcon
            height={20}
            width={20}
          />
        </Styled.NavbarNavigation>
      </Tooltip>
      <Styled.DetailsTitle data-testid={identifiers["navbar.detailsTitle"]}>
        {consumer.user && <p>{`${getUserName(consumer.user)}`}</p>}
      </Styled.DetailsTitle>
    </NavigationCustomElement>
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (!selectedWorkspace || !selectedApp || !consumer.user) {
    return <Spinner />;
  }

  const handleChangeUserMode = (isBanned?: boolean) => {
    consumer.changeUserMode(isBanned);
    setModeDropdownOpened(false);
  };

  const addToFilterObject = {
    addToFilter: addExternal,
    redirectURL: generatePath(RoutePaths.USERS, {
      workspaceSlug: selectedWorkspace?.slug,
      appKey: selectedApp?.key,
    }),
  };

  const userName = getUserName(consumer.user);

  return (
    <PageWrap>
      <TicketContainer>
        <TicketInfoContainer>
          <Heading
            as="h2"
            heading2
            testId={identifiers["central.column.users.header"]}
            marginBottom={0}
          >
            {userName}
          </Heading>
          <Flex
            $gap={1}
            $alignItems="center"
            style={{ paddingTop: "2.8rem" }}
          >
            <UserTooltip
              position="top"
              text="Set user mode"
            >
              <UserModeDropdown
                label={userModes.find((mode) => mode.value === consumer.user?.banned)?.label}
                iconComponent={getModeIcon(consumer.user.banned)}
                buttonElement={<Fragment />}
                position="left"
                isOpened={modeDropdownOpened}
                setIsOpened={setModeDropdownOpened}
              >
                <Subtitle fontSize={12}>Set user mode</Subtitle>
                {userModes.map((mode) => {
                  return (
                    <DropdownItem
                      key={mode.label}
                      onClick={() => handleChangeUserMode(mode.value)}
                    >
                      {getModeIcon(mode.value)}
                      {mode.label}
                    </DropdownItem>
                  );
                })}
              </UserModeDropdown>
            </UserTooltip>
            {!consumer.user.banned && (
              <HelperText>
                {"Learn more about"}&nbsp;
                <a onClick={() => showIntercomArticle(helpCenterArticles.blockUsers)}>{"blocking spammy users"}</a>
                {"."}
              </HelperText>
            )}
          </Flex>
          <UserTicketCards
            userTickets={consumer.user.tickets}
            unreadNotifications={unreadNotifications}
          />
        </TicketInfoContainer>

        <TicketDetailsContainer>
          <DetailsPane
            info={getUserDetails(
              consumer.user,
              detailDropdownOpened,
              setDetailDropdownOpened,
              selectedApp?.platform.name,
              addToFilterObject,
            )}
            headerTestId={identifiers["users.details.pane.header"]}
          />
        </TicketDetailsContainer>
      </TicketContainer>
    </PageWrap>
  );
}

function getUserDetails(
  user?: AppUser | null,
  detailDropdownOpened?: string,
  setDetailDropdownOpened?: (opened?: string) => void,
  platformName?: PlatformName,
  addToFilterObject?: {
    addToFilter: (attributeName: string, filterValue: string) => void;
    redirectURL: string;
  },
): GroupedInfoElement[] {
  if (!user) return [];

  const keysToOmit = ["first_name", "last_name"];

  return [
    {
      title: "User details",
      infoElements: [
        {
          infoElements: [
            {
              isError: false,
              Dropdown: getDropdown(
                user.end_user_id,
                "User ID",
                <Icon.UserID style={{ width: "20px", height: "20px" }} />,
                detailDropdownOpened,
                setDetailDropdownOpened,
                "User ID",
                addToFilterObject,
                user.end_user_id,
              ),
              fullwidth: true,
            },
          ],
        },
      ],
    },
    {
      title: "User metadata",
      infoElements: [
        { infoElements: readMetadata(user.metadata_, keysToOmit, detailDropdownOpened, setDetailDropdownOpened) },
      ],
      link: {
        tooltipText: "Set user metadata",
        link: docLink(platformName).addUserMetadata,
        isExternal: true,
      },
    },
  ];
}

const getModeIcon = (isBlocked?: boolean) => {
  if (isBlocked)
    return (
      <ModeIcon>
        <Icon.BlockedIcon />
      </ModeIcon>
    );
  return (
    <ModeIcon>
      <UserIcon />
    </ModeIcon>
  );
};

export const userModes = [
  { label: "Active", value: false },
  { label: "Blocked", value: true },
];
